/*
 * Quantity pricing in the Elixir cart drawer.
 *
 * Bundle discounts ("Buy 2, save 10%") live in the checkout's CRM (Settings ‚Üí
 * Quantity pricing) and are charged by the checkout, not by Shopify. Shopify's
 * automatic discounts can't count only one product toward a minimum (Priority
 * Shipping and Shipping Protection count too), so the drawer instead shows
 * the CRM's prices: it reads the same tiers from the checkout API
 * (GET {origin}/api/storefront/offers) and prices the cart the way the
 * checkout does (packages/shared/src/pricing.ts):
 *   - units of one product are counted together across its variants;
 *   - the best tier whose quantity is <= those units applies;
 *   - percent tiers: discount = round(subtotal * pct / 100);
 *     bundle-price tiers: total = round(price * units / tierQuantity);
 *   - the product's discount is split across its lines (largest remainder).
 * It rewrites each discounted line's price and "SAVE" tag, the Savings row and
 * the Total, and re-applies whenever the drawer re-renders.
 */
(function () {
  'use strict';

  var script = document.querySelector('script[src*="external-checkout.js"]');
  var origin = ((script && script.getAttribute('data-origin')) || '').replace(/[\s/.]+$/, '');
  var cache = {};

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function loadOffers(productIds) {
    var ids = productIds.filter(function (id) { return !(id in cache); });
    if (!origin || !ids.length) return Promise.resolve(cache);
    return fetch(origin + '/api/storefront/offers?products=' + ids.join(','), { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : { offers: {} }; })
      .then(function (body) {
        ids.forEach(function (id) {
          var o = body && body.offers && body.offers[id];
          cache[id] = o && o.tiers ? o.tiers : [];
        });
        return cache;
      })
      .catch(function () { return cache; });
  }

  function tierFor(tiers, units) {
    var best = null;
    (tiers || []).forEach(function (t) {
      if (t.quantity <= units && (!best || t.quantity > best.quantity)) best = t;
    });
    return best;
  }

  function tierTotal(tier, units, subtotal) {
    var total;
    if (tier.kind === 'percent') {
      var pct = Math.min(100, Math.max(0, tier.value));
      total = subtotal - Math.round((subtotal * pct) / 100);
    } else {
      total = Math.round((tier.value * units) / tier.quantity);
    }
    return Math.max(0, Math.min(subtotal, total));
  }

  function split(amount, weights) {
    var sum = weights.reduce(function (a, b) { return a + b; }, 0);
    if (!sum) return weights.map(function () { return 0; });
    var raw = weights.map(function (w) { return (amount * w) / sum; });
    var parts = raw.map(Math.floor);
    var left = amount - parts.reduce(function (a, b) { return a + b; }, 0);
    raw
      .map(function (r, i) { return { i: i, frac: r - Math.floor(r) }; })
      .sort(function (a, b) { return b.frac - a.frac || a.i - b.i; })
      .slice(0, left)
      .forEach(function (x) { parts[x.i] += 1; });
    return parts;
  }

  /** Cents off each line (by index) and in total, from the CRM's tiers. */
  function price(cart) {
    var items = (cart && cart.items) || [];
    var ids = [];
    items.forEach(function (it) { if (ids.indexOf(String(it.product_id)) === -1) ids.push(String(it.product_id)); });
    return loadOffers(ids).then(function (offers) {
      var result = { total: 0, lines: items.map(function () { return 0; }) };
      ids.forEach(function (pid) {
        var idx = [];
        items.forEach(function (it, i) { if (String(it.product_id) === pid) idx.push(i); });
        var units = idx.reduce(function (s, i) { return s + items[i].quantity; }, 0);
        var tier = tierFor(offers[pid], units);
        if (!tier) return;
        var values = idx.map(function (i) { return items[i].price * items[i].quantity; });
        var subtotal = values.reduce(function (a, b) { return a + b; }, 0);
        var discount = subtotal - tierTotal(tier, units, subtotal);
        if (discount <= 0) return;
        split(discount, values).forEach(function (d, j) { result.lines[idx[j]] = d; });
        result.total += discount;
      });
      return result;
    });
  }

  function cents(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : 0;
  }

  /**
   * What a line costs before any discount. Shopify's cart.js has no compare-at
   * price, so this reads the struck-through price the drawer printed (compare-at
   * x quantity), falling back to the line's regular price.
   */
  function listLine(item, row) {
    var printed = row ? cents((row.querySelector('.cart-item__price-wrapper .cart-item__old-price') || {}).textContent) : 0;
    return Math.max(printed, item.original_line_price || item.price * item.quantity);
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function apply(cart, off) {
    var cur = cart.currency;
    var list = 0;
    (cart.items || []).forEach(function (item, i) {
      var row = document.getElementById('CartDrawer-Item-' + (i + 1));
      var old = listLine(item, row);
      list += old;
      var d = off.lines[i] || 0;
      if (!d) return;
      var box = row && row.querySelector('.cart-item__price-wrapper .gb-price-drawer');
      if (!box) return;
      var finalLine = Math.max(0, item.final_line_price - d);
      var oldEl = box.querySelector('.cart-item__old-price');
      var newEl = box.querySelector('.cart-item__final-price');
      if (!oldEl || !newEl) {
        box.textContent = '';
        oldEl = document.createElement('s');
        oldEl.className = 'cart-item__old-price';
        newEl = document.createElement('strong');
        newEl.className = 'cart-item__final-price';
        box.appendChild(oldEl);
        box.appendChild(newEl);
      }
      setText(oldEl, money(old, cur));
      setText(newEl, money(finalLine, cur));
      var wrapper = box.parentNode;
      var save = wrapper.querySelector('.gb-price-drawer-saving');
      if (!save) {
        save = document.createElement('div');
        save.className = 'gb-price-drawer-saving';
        wrapper.appendChild(save);
      }
      setText(save, 'SAVE ' + money(old - finalLine, cur).replace(/\.00$/, ''));
    });

    var total = Math.max(0, cart.total_price - off.total);
    document.querySelectorAll('.totals__total-value').forEach(function (el) { setText(el, money(total, cur)); });
    document.querySelectorAll('.gb-totals-total-value').forEach(function (el) { setText(el, String(total)); });
    if (list > total) {
      document.querySelectorAll('.discount_discount-value span').forEach(function (el) { setText(el, '-' + money(list - total, cur)); });
    }
  }

  var seq = 0;
  function refresh() {
    if (!document.getElementById('CartDrawer-Item-1')) return;
    var mine = (seq += 1);
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || mine !== seq) return null;
        return price(cart).then(function (off) {
          if (mine !== seq || !off.total) return;
          observer.disconnect();
          try {
            apply(cart, off);
          } finally {
            watch();
          }
        });
      })
      .catch(function () { /* the drawer keeps Shopify's prices */ });
  }

  // The drawer re-renders its items and totals after every cart change. Only
  // those redraws count: the drawer's countdown and other live text change
  // every second and must not re-read the cart.
  var CART_PARTS = '.cart-item, .totals__total-value, .discount_discount-value, cart-drawer-items';
  function touchesCart(records) {
    for (var r = 0; r < records.length; r++) {
      var nodes = records[r].addedNodes;
      for (var n = 0; n < nodes.length; n++) {
        var node = nodes[n];
        if (node.nodeType !== 1) continue;
        if (node.matches(CART_PARTS) || node.querySelector(CART_PARTS)) return true;
      }
    }
    return false;
  }
  var timer = null;
  var observer = new MutationObserver(function (records) {
    if (!touchesCart(records)) return;
    clearTimeout(timer);
    timer = setTimeout(refresh, 60);
  });
  function watch() {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { watch(); refresh(); });
  else { watch(); refresh(); }
})();
